/**
 * TilemapView — the React wrapper hosting the framework-free TilemapSurface and
 * its docked panels. TM2 completes the editing UI: Tile Palette + Layers on the
 * left, Map Inspector on the right, the full Brush/Erase/Rect/Fill/Picker
 * toolset, brush size, grid + chunk-debug toggles, and a save indicator. The
 * imperative paint surface lives in TilemapSurface; this component owns the
 * DOM node, the surface lifecycle, the toolbar, and view-scoped keyboard.
 */

import { useEffect, useRef, useState } from 'react';
import { TilemapSurface } from './surface/TilemapSurface';
import { useTilemapStore, tilemapStore } from './store/tilemapStore';
import type { Tool } from './model/types';
import { TilePalettePanel } from './panels/TilePalettePanel';
import { LayersPanel } from './panels/LayersPanel';
import { MapInspector } from './panels/MapInspector';
import { TilemapExportImportDialog } from './panels/TilemapExportImportDialog';
import { Minimap } from './panels/Minimap';
import { AutotileRuleEditor } from './panels/AutotileRuleEditor';

export function TilemapView({ projectUuid, onExit }: { projectUuid: string; onExit: () => void }) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const surfaceRef = useRef<TilemapSurface | null>(null);
    const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
    const [showGrid, setShowGrid] = useState(true);
    const [showChunkDebug, setShowChunkDebug] = useState(false);
    const [showCollisions, setShowCollisions] = useState(false);
    const [leftTab, setLeftTab] = useState<'palette' | 'layers' | 'autotile'>('palette');
    const [inspectorOpen, setInspectorOpen] = useState(true);
    const [showExportImport, setShowExportImport] = useState(false);

    const doc = useTilemapStore((s) => s.doc);
    const tool = useTilemapStore((s) => s.tool);
    const brushSize = useTilemapStore((s) => s.brushSize);
    const stamp = useTilemapStore((s) => s.stamp);
    const status = useTilemapStore((s) => s.status);
    const errorMessage = useTilemapStore((s) => s.errorMessage);
    const activeTerrain = useTilemapStore((s) => s.activeTerrain);
    const activeLayerUuid = useTilemapStore((s) => s.activeLayerUuid);

    // Terrains available on the active layer's tileset, for the toolbar dropdown.
    const activeLayerMeta = doc?.layers.find((l) => l.uuid === activeLayerUuid);
    const terrainOptions = activeLayerMeta
        ? Object.keys(doc?.tilesets.find((t) => t.uuid === activeLayerMeta.tileset_uuid)?.autotile_rules ?? {})
        : [];

    useEffect(() => {
        if (!canvasRef.current) return;
        const surface = new TilemapSurface(canvasRef.current);
        surfaceRef.current = surface;
        return () => surface.destroy();
    }, []);

    useEffect(() => { surfaceRef.current?.setShowGrid(showGrid); }, [showGrid]);
    useEffect(() => { surfaceRef.current?.setShowChunkDebug(showChunkDebug); }, [showChunkDebug]);
    useEffect(() => { surfaceRef.current?.setShowCollisions(showCollisions); }, [showCollisions]);

    // View-scoped keyboard (tools + undo/redo + brush size). Active only while
    // this view is mounted, so the graph view's bindings never collide.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const t = e.target as HTMLElement | null;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            const s = tilemapStore.getState();
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                if (e.shiftKey) s.redoAction(); else s.undoAction();
                return;
            }
            switch (e.key.toLowerCase()) {
                case 'b': s.setTool('brush'); break;
                case 'e': s.setTool('erase'); break;
                case 'r': s.setTool('rect'); break;
                case 'f': s.setTool('fill'); break;
                case 'i': s.setTool('picker'); break;
                case 't': s.setTool('autotile'); break;
                case 'v': s.setTool('none'); break;
                case '[': s.setBrushSize(s.brushSize - 1); break;
                case ']': s.setBrushSize(s.brushSize + 1); break;
                case 'escape': if (s.tool === 'none') onExit(); else s.setTool('none'); break;
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onExit]);

    const setTool = (t: Tool) => tilemapStore.getState().setTool(t);
    const singleTile = stamp.w === 1 && stamp.h === 1;

    return (
        <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
            {/* Toolbar */}
            <div className="d-flex align-items-center gap-2 px-2 py-1 border-bottom flex-wrap">
                <div className="btn-group btn-group-sm">
                    <ToolBtn tool="brush" cur={tool} onClick={setTool} icon="brush" title="Brush (B)" />
                    <ToolBtn tool="erase" cur={tool} onClick={setTool} icon="eraser" title="Erase (E) — RMB always erases" />
                    <ToolBtn tool="rect" cur={tool} onClick={setTool} icon="square" title="Rectangle (R)" />
                    <ToolBtn tool="fill" cur={tool} onClick={setTool} icon="bucket-droplet" title="Fill (F)" />
                    <ToolBtn tool="picker" cur={tool} onClick={setTool} icon="color-picker" title="Picker (I / Alt+click)" />
                    <ToolBtn tool="autotile" cur={tool} onClick={setTool} icon="stack-2" title="Autotile (T) — paints a terrain, auto-picking the correct edge/corner tile" />
                    <ToolBtn tool="none" cur={tool} onClick={setTool} icon="hand-stop" title="Pan / select (V)" />
                </div>

                {tool === 'autotile' && (
                    <select
                        className="form-select form-select-sm"
                        style={{ width: 140 }}
                        value={activeTerrain ?? ''}
                        onChange={(e) => tilemapStore.getState().setActiveTerrain(e.target.value || null)}
                    >
                        <option value="">Choose terrain…</option>
                        {terrainOptions.map((name) => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                )}

                <div className="d-flex align-items-center gap-1" title={singleTile ? 'Brush size (N×N block)' : 'Brush size applies to single-tile stamps only'}>
                    <span className="text-secondary small">Size</span>
                    <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => tilemapStore.getState().setBrushSize(brushSize - 1)}>◂</button>
                    <span style={{ minWidth: 18, textAlign: 'center' }}>{brushSize}</span>
                    <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => tilemapStore.getState().setBrushSize(brushSize + 1)}>▸</button>
                </div>

                <span className="text-secondary small">Stamp {stamp.w}×{stamp.h}</span>

                <label className="form-check form-switch mb-0 ms-1">
                    <input className="form-check-input" type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} />
                    <span className="form-check-label small">Grid</span>
                </label>
                <label className="form-check form-switch mb-0">
                    <input className="form-check-input" type="checkbox" checked={showChunkDebug} onChange={(e) => setShowChunkDebug(e.target.checked)} />
                    <span className="form-check-label small">Chunks</span>
                </label>
                <label className="form-check form-switch mb-0" title="Preview the engine's greedy-meshed colliders for layers with Generate Colliders on">
                    <input className="form-check-input" type="checkbox" checked={showCollisions} onChange={(e) => setShowCollisions(e.target.checked)} />
                    <span className="form-check-label small">Colliders</span>
                </label>

                <div className="ms-auto d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowExportImport(true)} title="Export to JoystickEngine / import an engine or Tiled zip">
                        <i className="ti ti-package me-1" /> Export / Import
                    </button>
                    <SaveIndicator status={status} />
                    <button className="btn btn-sm btn-outline-secondary" onClick={onExit} title="Back (Esc)">
                        <i className="ti ti-arrow-back-up me-1" /> Back
                    </button>
                </div>
            </div>

            {showExportImport && doc && (
                <TilemapExportImportDialog
                    projectUuid={projectUuid}
                    tilemapUuid={doc.tilemap.uuid}
                    tilemapName={doc.tilemap.name}
                    onClose={() => setShowExportImport(false)}
                />
            )}

            {/* Body: left dock · canvas · right dock */}
            <div className="d-flex flex-fill" style={{ minHeight: 0 }}>
                <div className="border-end d-flex flex-column" style={{ width: 260, flexShrink: 0 }}>
                    <div className="d-flex border-bottom">
                        <button className={`btn btn-sm flex-fill rounded-0 ${leftTab === 'palette' ? 'btn-light' : 'btn-white'}`} onClick={() => setLeftTab('palette')}>Palette</button>
                        <button className={`btn btn-sm flex-fill rounded-0 ${leftTab === 'layers' ? 'btn-light' : 'btn-white'}`} onClick={() => setLeftTab('layers')}>Layers</button>
                        <button className={`btn btn-sm flex-fill rounded-0 ${leftTab === 'autotile' ? 'btn-light' : 'btn-white'}`} onClick={() => setLeftTab('autotile')}>Autotile</button>
                    </div>
                    <div className="flex-fill overflow-hidden">
                        {leftTab === 'palette' && <TilePalettePanel projectUuid={projectUuid} />}
                        {leftTab === 'layers' && <LayersPanel projectUuid={projectUuid} />}
                        {leftTab === 'autotile' && <AutotileRuleEditor projectUuid={projectUuid} />}
                    </div>
                </div>

                <div className="flex-fill position-relative" style={{ minHeight: 0, background: 'var(--tblr-body-bg)' }}>
                    <canvas
                        ref={(el) => { canvasRef.current = el; setCanvasEl(el); }}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', touchAction: 'none', cursor: tool === 'none' ? 'grab' : 'crosshair' }}
                    />
                    {doc && doc.layers.length === 0 && (
                        <div className="position-absolute top-50 start-50 translate-middle text-secondary text-center">
                            This map has no layers yet.<br />Create a tileset in the Palette, then add a layer.
                        </div>
                    )}
                </div>

                <div className="border-start d-flex flex-column" style={{ width: inspectorOpen ? 260 : 36, flexShrink: 0 }}>
                    <button type="button" className="btn btn-sm btn-outline-secondary border-0 rounded-0" style={{ width: 36 }}
                        title={inspectorOpen ? 'Collapse' : 'Expand'} onClick={() => setInspectorOpen((o) => !o)}>
                        <i className={`ti ti-chevron-${inspectorOpen ? 'right' : 'left'}`} />
                    </button>
                    {inspectorOpen && (
                        <div className="overflow-auto flex-fill d-flex flex-column">
                            <Minimap mainCanvas={canvasEl} />
                            <div className="border-top flex-fill"><MapInspector projectUuid={projectUuid} /></div>
                        </div>
                    )}
                </div>
            </div>

            {/* Status bar */}
            <div className="d-flex align-items-center gap-3 px-2 py-1 border-top small text-secondary">
                <span>{doc?.tilemap.name}</span>
                {errorMessage && <span className="text-danger">{errorMessage}</span>}
                <span className="ms-auto">B/E/R/F/I/T · [ ] size · Ctrl+Z undo · RMB erase · Alt+click pick</span>
            </div>
        </div>
    );
}

function ToolBtn({ tool, cur, onClick, icon, title }: { tool: Tool; cur: Tool; onClick: (t: Tool) => void; icon: string; title: string }) {
    return (
        <button className={`btn ${cur === tool ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => onClick(tool)} title={title}>
            <i className={`ti ti-${icon}`} />
        </button>
    );
}

function SaveIndicator({ status }: { status: string }) {
    const map: Record<string, { label: string; cls: string }> = {
        idle: { label: 'Ready', cls: 'text-secondary' },
        dirty: { label: 'Unsaved…', cls: 'text-warning' },
        saving: { label: 'Saving…', cls: 'text-info' },
        saved: { label: 'Saved', cls: 'text-success' },
        conflict: { label: 'Conflict — retrying', cls: 'text-danger' },
        error: { label: 'Save error', cls: 'text-danger' },
    };
    const s = map[status] ?? map.idle;
    return <span className={`small ${s.cls}`}>{s.label}</span>;
}
